import {DollarOutlined, CalendarOutlined, UsergroupAddOutlined} from "@ant-design/icons";
import {Space, Button, Tag, Tooltip} from "antd";
import {StyledCard, StyledClassCardContent} from "./card.style";
import {useNavigate} from "react-router-dom";
import {TLiveClassApiResponse} from "../../../../react-query-hooks/live-events/live-classes";
import {useJoinLiveEvent} from "../../../../features/live-events";
import {useSelector} from "react-redux";
import {RootState} from "../../../../redux/store";
import {handleMeetingLink} from "../../../../utils/meeting";
import {LiveClassCardMenu} from "./card-menu";
import {handleLiveClassLink} from "../../../../utils/live-class";
import moment from "moment";
import {LiveClassRescheduleModal} from "../reschedule/modal";
import {useState} from "react";

export type TLiveClassCardProps = Pick<
  TLiveClassApiResponse,
  | "name"
  | "description"
  | "callPrice"
  | "callPriceCurrency"
  | "activeSession"
  | "attendees"
  | "cover_image"
  | "statusName"
  | "slug"
  | "tiny_url"
  | "session_info"
  | "is_custom_room"
  | "video_platform"
  | "roomId"
> & {
  eventId: number;
  is_co_host?: boolean;
  hostUsername?: string;
  showDrawer?: (eventId: string | number) => void;
};

export function LiveClassCard({
  name,
  description,
  hostUsername,
  callPrice,
  callPriceCurrency,
  activeSession,
  attendees,
  cover_image,
  eventId,
  statusName,
  slug,
  tiny_url,
  is_co_host,
  session_info,
  is_custom_room,
  roomId,
  video_platform,
}: TLiveClassCardProps) {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const navigate = useNavigate();
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const joinLiveEventMutation = useJoinLiveEvent();

  function handleJoinLiveClass() {
    if (is_custom_room || video_platform.toLowerCase() === "custom") {
      setTimeout(() => {
        window.open(`//${roomId}`);
      }, 0);
    } else {
      joinLiveEventMutation.mutate(eventId, {
        onSuccess: (result) => {
          setTimeout(() => {
            const platform = result.videoPlatform.toLowerCase();
            if (platform === "dailyco") {
              window.open(`${result.dailyCoUrl}?t=${result.token}`);
            } else if (platform === "zoom") {
              window.open(result.roomId);
            } else if (platform === "googlemeet") {
              if (result.roomId) {
                window.open(result.roomId);
              }
            } else {
              window.open(
                handleMeetingLink("host", {
                  token: result.token,
                  roomId: result.roomId,
                  firstName: userInfo?.user?.firstName ?? "",
                  lastName: userInfo?.user?.lastName ?? "",
                  meetingTitle: name,
                })
              );
            }
          }, 0);
        },
      });
    }
  }

  // const disableButton = () => {
  //   if (statusName.toLowerCase() !== "in progress") {
  //     return true;
  //   } else {
  //     return false;
  //   }
  // };
  let stripedHtml = description.replace(/<[^>]+>/g, "");
  const eventDescription = stripedHtml;

  const classDate = moment(activeSession?.startDate).format("ddd, MMM DD YYYY");

  const showDrawer = () => {
    setOpenRescheduleModal(true);
  };

  const onClose = () => {
    setOpenRescheduleModal(false);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button, a, .ant-dropdown-trigger")) {
      return;
    }

    if (is_co_host && hostUsername) {
      window.open(
        handleLiveClassLink({
          eventId,
          slug,
          tinyUrl: tiny_url,
          username: hostUsername,
          windowNav: true,
        }),
        "_blank"
      );
    } else {
      navigate(`/live-classes/edit/${eventId}?activeTab=form`);
    }
  };

  return (
    <>
      <StyledCard
        title={name}
        {...(!is_co_host && {
          extra: (
            <LiveClassCardMenu
              eventId={eventId}
              is_co_host={is_co_host}
              hostUsername={hostUsername}
              slug={slug}
              tiny_url={tiny_url}
            />
          ),
        })}
      >
        <StyledClassCardContent onClick={handleCardClick}>
          <div className="class-card-image-wrapper">
            <img className="class-card-image" src={cover_image} alt={name} />
          </div>
          <p className="class-card-info-text">{eventDescription}</p>
          {/* <div
          className="class-card-info-text"
          dangerouslySetInnerHTML={{__html: description}}
        /> */}
          {/* <p>{name ?? "This is a draft name"}</p> */}
          <div className="class-card-extra-info">
            <div>
              <Space size={10}>
                <DollarOutlined style={{color: "#222222"}} />
                <span>
                  {callPrice === 0
                    ? "Free"
                    : callPriceCurrency + " " + callPrice.toLocaleString("en-US")}
                </span>
              </Space>
            </div>
            <div>
              <Space size={10}>
                <CalendarOutlined style={{color: "#222222"}} />
                <span>
                  {classDate}, {activeSession?.startTime}
                </span>
              </Space>
            </div>
            <div>
              <Space size={10}>
                <UsergroupAddOutlined style={{color: "#222222"}} />
                <span>{attendees === -1 ? "Unlimited" : attendees} attending</span>
              </Space>
            </div>
          </div>
          <div className="reg-tag">
            {is_co_host && (
              <div>
                <Tag color="green">co-host</Tag>
              </div>
            )}
            <div>
              {/* <Tag color="green">Co-host</Tag> */}
              {statusName === "Paused" && <Tag>Registration closed</Tag>}
            </div>
          </div>

          <Tooltip title={is_co_host ? "Join event" : "Start event"}>
            <div className="class-card-start-btn">
              <Button
                onClick={handleJoinLiveClass}
                type="primary"
                block
                size="small"
                loading={joinLiveEventMutation.isLoading}
              >
                {is_co_host ? "Join event" : "Start event"}
              </Button>
            </div>
          </Tooltip>
          {!is_co_host && (
            <div className="class-card-details-btn">
              <Button onClick={showDrawer} block size="small">
                Reschedule Event
              </Button>
            </div>
          )}
        </StyledClassCardContent>
      </StyledCard>
      {openRescheduleModal && (
        <LiveClassRescheduleModal
          eventId={eventId}
          onClose={onClose}
          visible={openRescheduleModal}
        />
      )}
    </>
  );
}
